import { useState } from "react"
import { useQuery } from "@apollo/client"
import { useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { GripVertical, Trash2 } from "lucide-react"
import { List, arrayMove } from "react-movable"

import { Button } from "~/shadcn/ui/button"
import { Form } from "~/shadcn/ui/form"
import { TextField } from "~/ui/forms/TextField"
import { Error } from "~/ui/Error"

import { useSafeMutation } from "~/common/useSafeMutation"
import { gql } from "~/__generated__"
import { ReviewStatus } from "~/__generated__/graphql"
import { ConfirmDialog } from "~/ui/Confirm"

const reviewStatusFormSchema = z.object({
  name: z.string().min(1, {
    message: "Required",
  }),
})

export const ReviewStatuses = ({
  organizationId,
}: {
  organizationId: string
}) => {
  const [reviewStatuses, setReviewStatuses] = useState<ReviewStatus[]>([])
  const {
    data,
    loading: reviewStatusesLoading,
    error,
  } = useQuery(REVIEW_STATUSES_QUERY_DOCUMENT, {
    variables: {
      organizationId: organizationId,
    },
    onCompleted: (data) => {
      setReviewStatuses(data.reviewStatuses?.nodes || [])
      return data
    },
  })

  const form = useForm<z.infer<typeof reviewStatusFormSchema>>({
    reValidateMode: "onChange",
    resolver: zodResolver(reviewStatusFormSchema),
    defaultValues: {
      name: "",
    },
  })

  const [createReviewStatus, { loading }] = useSafeMutation(
    REVIEW_STATUS_CREATE_MUTATION
  )

  const [updateReviewStatus] = useSafeMutation(REVIEW_STATUS_UPDATE_MUTATION)

  const [destroyReviewStatus] = useSafeMutation(REVIEW_STATUS_DESTROY_MUTATION)

  const onSubmit = async (values: z.infer<typeof reviewStatusFormSchema>) => {
    const { data, errors } = await createReviewStatus({
      variables: {
        input: {
          organizationId,
          name: values.name,
        },
      },
    })

    const reviewStatus = data?.reviewStatusCreate?.reviewStatus

    if (errors) {
      toast.error("Error saving review status")
      console.log(errors)
    } else if (!reviewStatus) {
      toast.error("Error saving review status")
    } else {
      form.reset()

      setReviewStatuses((prev) => [
        ...prev,
        {
          id: reviewStatus.id,
          name: reviewStatus.name,
          position: reviewStatus?.position,
        },
      ])
      toast.success("Review status saved!")
    }
  }

  const onDestroy = async (id: string) => {
    const { errors } = await destroyReviewStatus({
      variables: {
        input: {
          reviewStatusId: id,
        },
      },
    })

    if (errors) {
      toast.error(`Error deleting review status: ${errors}`)
      console.log(errors)
    } else {
      setReviewStatuses((prev) =>
        prev.filter((reviewStatus) => reviewStatus.id !== id)
      )
      toast.success("Review status deleted!")
    }
  }

  const handleMovePositions = async (draggedId: string, newIndex: number) => {
    const { data, errors } = await updateReviewStatus({
      variables: {
        input: {
          reviewStatusId: draggedId,
          position: newIndex,
        },
      },
    })
    if (errors) {
      toast.error("Error reordering.")
      console.log(errors)
    } else if (!data?.reviewStatusUpdate?.reviewStatus) {
      toast.error("Error reordering.")
    }
  }

  if (error || !data) return <Error message="Error loading review statuses." />

  return (
    <div className="flex flex-col rounded-lg divide-y divide-y-gray-E6E6E3 border border-gray-E6E6E3">
      <div className="p-6 bg-gray-F7F7F5 flex flex-col gap-1">
        <h2 className="text-gray-333 font-semibold">Review Statuses</h2>
        <p className="text-gray-999 text-sm">
          Create your own review statuses. You can re-order or delete them at
          any time.
        </p>
      </div>
      <div className="pt-4 pb-6 px-6 flex flex-col">
        <List
          disabled={loading}
          values={reviewStatuses}
          onChange={({ oldIndex, newIndex }) => {
            setReviewStatuses(arrayMove(reviewStatuses, oldIndex, newIndex))
            handleMovePositions(
              reviewStatuses?.[oldIndex]?.id ?? "",
              newIndex + 1
            )
          }}
          renderList={({ children, props }) => (
            <div {...props} className="flex flex-col gap-1">
              {children}
            </div>
          )}
          renderItem={({ value: reviewStatus, props, isDragged }) => {
            const row = (
              <div
                key={reviewStatus.id}
                className="flex items-center justify-between gap-0 px-4 py-1 h-[44px] bg-white border border-white hover:bg-gray-F7F7F5 hover:border-gray-E6E6E3 rounded-lg group"
              >
                <div className="flex items-center gap-4">
                  <GripVertical size={14} className="text-[#8e8e8e]" />
                  <div className="text-gray-333 font-medium">
                    {reviewStatus.name}
                  </div>
                </div>
                <div className="group-hover:visible invisible">
                  <ConfirmDialog
                    text={
                      <>
                        <span className="font-medium mb-1">
                          Are you sure you would like to delete status "
                          {reviewStatus.name}"?
                        </span>
                        <br />
                        <span className="text-gray-999">
                          If you have any candidates in the deleted status, they
                          will be moved to the first status in the list.
                        </span>
                      </>
                    }
                    onSuccess={() => onDestroy(reviewStatus.id)}
                  >
                    <Button variant="ghost" size="icon">
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </ConfirmDialog>
                </div>
              </div>
            )

            return isDragged ? (
              <div {...props}>
                <div>{row}</div>
              </div>
            ) : (
              row
            )
          }}
        />

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex gap-4 mt-4"
          >
            <div className="w-full flex items-center gap-4 border border-gray-E6E6E3 rounded-lg p-2">
              <TextField
                control={form.control}
                name="name"
                label=""
                placeholder="Create new status"
                containerClassName="w-full"
                inputClassName="w-full border-none focus:ring-0"
                showLabel={false}
              />
              <Button
                type="submit"
                size="sm"
                disabled={reviewStatusesLoading || loading}
                onClick={() => console.log("errors:", form.formState.errors)}
              >
                Add
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}

const REVIEW_STATUSES_QUERY_DOCUMENT = gql(/* GraphQL */ `
  query reviewStatuses($organizationId: ID!) {
    reviewStatuses(organizationId: $organizationId) {
      nodes {
        id
        name
        position
      }
    }
  }
`)

const REVIEW_STATUS_CREATE_MUTATION = gql(`
  mutation ReviewStatusCreate($input: ReviewStatusCreateInput!) {
    reviewStatusCreate(input: $input) {
      reviewStatus {
        id
        name
        position
      }
    }
  }
`)

// This is currently used just for reordering review statuses
const REVIEW_STATUS_UPDATE_MUTATION = gql(`
  mutation ReviewStatusUpdate($input: ReviewStatusUpdateInput!) {
    reviewStatusUpdate(input: $input) {
      reviewStatus {
        id
        name
        position
      }
    }
  }
`)

const REVIEW_STATUS_DESTROY_MUTATION = gql(`
  mutation ReviewStatusDestroy($input: ReviewStatusDestroyInput!) {
    reviewStatusDestroy(input: $input) {
      reviewStatus {
        id
      }
    }
  }
`)
